import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  Brain,
  Database,
  Server,
  type LucideIcon,
} from "lucide-react";
import {
  getSystemHealth,
  runIntelligenceScan,
  type SystemHealthResponse,
} from "@/api/systemHealth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { QueryErrorState } from "@/components/ui/QueryErrorState";
import { LoadingPage } from "@/components/ui/LoadingPage";
import { PageContainer, EmptyState } from "@/design-system";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { useChurchTimezone } from "@/hooks/useChurchTimezone";
import { formatLastLogin } from "@/lib/last-login";

export function SystemHealthPage() {
  const { churchId } = useAuth();
  const { permissions, isLoading: permissionsLoading } =
    useEffectivePermissions();
  const canManage = permissions.has("SETTINGS_MANAGE");
  const timeZone = useChurchTimezone(churchId, canManage);
  const queryClient = useQueryClient();
  const health = useQuery({
    queryKey: ["system-health", churchId],
    enabled: !!churchId && canManage,
    queryFn: () => getSystemHealth(churchId!),
  });
  const scan = useMutation({
    mutationFn: () => runIntelligenceScan(churchId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["system-health", churchId] });
      queryClient.invalidateQueries({ queryKey: ["care-queue", churchId] });
    },
  });
  if (permissionsLoading) return <LoadingPage />;
  if (!canManage)
    return (
      <PageContainer>
        <EmptyState
          title="Administration access required"
          description="Your current assignments do not grant permission to manage church settings."
        />
      </PageContainer>
    );
  if (health.isLoading) return <LoadingPage />;
  if (health.isError || !health.data)
    return (
      <PageContainer>
        <QueryErrorState
          title="Unable to load System Health"
          onRetry={() => void health.refetch()}
        />
      </PageContainer>
    );
  return (
    <PageContainer>
      <SystemHealthBanner health={health.data} />
      <ChurchHealthDetails health={health.data} timeZone={timeZone} />
      <Card>
        <CardContent className="space-y-4 p-6">
          <Button disabled={scan.isPending} onClick={() => scan.mutate()}>
            {scan.isPending ? "Running Scan..." : "Run Intelligence Scan"}
          </Button>
          <p className="text-sm text-slate-400">
            Manual scans process only your church. Use them after imports or for
            administrative validation.
          </p>
          {scan.isSuccess && <ChurchScanStatus status={scan.data.status} />}
          {scan.isError && (
            <p role="alert" className="text-rose-300">
              Unable to complete your church's intelligence scan.
            </p>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}

function SystemHealthBanner({ health }: { health: SystemHealthResponse }) {
  const statuses = [
    health.api_status,
    health.database_status,
    health.intelligence.status,
  ].map((status) => status.toUpperCase());
  const overall = statuses.every((status) => status === "HEALTHY")
    ? {
        label: "Healthy",
        variant: "success" as const,
        message: "API, Database, and Intelligence are healthy for your church.",
      }
    : statuses.some((status) =>
          ["UNHEALTHY", "UNAVAILABLE", "FAILED"].includes(status),
        )
      ? {
          label: "Needs attention",
          variant: "danger" as const,
          message:
            "One or more services are unavailable or unhealthy. Review the service statuses below.",
        }
      : statuses.some(
            (status) => !["HEALTHY", "STALE", "DEGRADED"].includes(status),
          )
        ? {
            label: "Status unavailable",
            variant: "warning" as const,
            message:
              "One or more service statuses are unknown. Overall health cannot be confirmed.",
          }
        : {
            label: "Degraded",
            variant: "warning" as const,
            message:
              "One or more services are stale or degraded. Review the service statuses below.",
          };

  return (
    <section
      aria-label="Overall System Health"
      className="rounded-3xl border border-slate-800 bg-gradient-to-r from-slate-900 via-slate-900 to-slate-950 p-7"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-indigo-500/15 text-indigo-400">
          <Activity size={30} aria-hidden="true" focusable="false" />
        </div>
        <div>
          <p className="text-sm font-medium text-indigo-400">Settings</p>
          <h1 className="mt-1 text-3xl font-bold text-white">System Health</h1>
          <div role="status" className="mt-3 space-y-2">
            <Badge variant={overall.variant}>
              Overall status: {overall.label}
            </Badge>
            <p className="text-sm text-slate-400">{overall.message}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ChurchScanStatus({
  status,
}: {
  status: "SUCCESS" | "SKIPPED_LOCKED";
}) {
  return (
    <p role="status">
      {status === "SKIPPED_LOCKED"
        ? "A scan for your church is already in progress. Try again shortly."
        : "Intelligence scan complete. Intelligence for your church has been refreshed."}
    </p>
  );
}

export function ChurchHealthDetails({
  health,
  timeZone,
}: {
  health: SystemHealthResponse;
  timeZone?: string;
}) {
  const intelligence = health.intelligence;
  return (
    <>
      <div className="grid gap-4 xl:grid-cols-3">
        <HealthCard title="API" status={health.api_status} icon={Server} />
        <HealthCard
          title="Database"
          status={health.database_status}
          icon={Database}
        />
        <HealthCard
          title="Intelligence"
          status={intelligence.status}
          icon={Brain}
        />
      </div>
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold">Intelligence Engine</h2>
          {!intelligence.last_successful_scan_at && (
            <p className="mt-3 text-slate-400">
              No successful scan recorded for your church.
            </p>
          )}
          {intelligence.status === "STALE" && (
            <p className="mt-3 text-amber-300">
              The last successful scan is older than the freshness target.
            </p>
          )}
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Metric
              label="Status"
              value={
                { HEALTHY: "Healthy", STALE: "Stale", UNKNOWN: "Unknown" }[
                  intelligence.status
                ]
              }
            />
            <Metric
              label="Last successful scan"
              value={
                intelligence.last_successful_scan_at
                  ? formatLastLogin(
                      intelligence.last_successful_scan_at,
                      timeZone,
                    )
                  : "Not recorded"
              }
            />
            <Metric
              label="Members evaluated"
              value={intelligence.members_evaluated}
            />
            <Metric
              label="Households evaluated"
              value={intelligence.households_evaluated}
            />
            <Metric
              label="Calculation duration"
              value={
                intelligence.duration_ms === null
                  ? null
                  : `${intelligence.duration_ms} ms`
              }
            />
            <Metric
              label="Needs Attention"
              value={intelligence.open_care_items}
            />
            <Metric
              label="In Progress"
              value={intelligence.in_progress_care_items}
            />
          </div>
          <p className="mt-4 text-sm text-slate-400">
            Freshness target: {intelligence.freshness_target_minutes} minutes.
            Exact next scan time is unavailable. Counts cover church-wide
            members; calculation duration excludes the final commit.
          </p>
        </CardContent>
      </Card>
    </>
  );
}

function HealthCard({
  title,
  status,
  icon: Icon,
}: {
  title: string;
  status: string;
  icon: LucideIcon;
}) {
  const normalizedStatus = status.toUpperCase();
  const treatment =
    normalizedStatus === "HEALTHY"
      ? { background: "bg-emerald-500/15", text: "text-emerald-400" }
      : normalizedStatus === "STALE" || normalizedStatus === "DEGRADED"
        ? { background: "bg-amber-500/15", text: "text-amber-400" }
        : normalizedStatus === "UNHEALTHY"
          ? { background: "bg-rose-500/15", text: "text-rose-400" }
          : { background: "bg-slate-800", text: "text-slate-300" };

  return (
    <Card>
      <CardContent className="p-5">
        <div
          role="group"
          aria-label={`${title} health`}
          className="flex items-center gap-4"
        >
          <div
            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${treatment.background} ${treatment.text}`}
          >
            <Icon size={22} aria-hidden="true" focusable="false" />
          </div>
          <div>
            <p className="text-sm text-slate-400">{title}</p>
            <p className={`mt-1 font-semibold ${treatment.text}`}>
              <span className="sr-only">Status: </span>
              {normalizedStatus}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Metric({
  label,
  value,
}: {
  label: string;
  value: string | number | null;
}) {
  return (
    <div className="min-w-0 rounded-xl border border-slate-800 p-4">
      <p className="text-sm text-slate-400">{label}</p>
      <p className="mt-2 break-words font-semibold">
        {value ?? "Not recorded"}
      </p>
    </div>
  );
}
